import React, { useEffect } from 'react';
import { Filter, X } from 'lucide-react';
import {
    ParkingLotsEditorFiltersState,
    countActiveEditorFilters,
    toggleParkingType,
    DEFAULT_PARKING_LOTS_EDITOR_FILTERS,
    type MapContextMode,
} from '../lib/parkingLotsEditorFilters';

const PARKING_TYPE_OPTIONS: { value: string; label: string }[] = [
    { value: 'free', label: 'Gratis' },
    { value: 'paid', label: 'Betalt' },
    { value: 'limited_time', label: 'Tidsbegrænset' },
];

interface ParkingLotsEditorFiltersProps {
    value: ParkingLotsEditorFiltersState;
    onChange: (next: ParkingLotsEditorFiltersState) => void;
    /** When true (editor map view), show the optional second map layer control. */
    showMapContextControls?: boolean;
    className?: string;
}

const ParkingLotsEditorFilters: React.FC<ParkingLotsEditorFiltersProps> = ({
    value,
    onChange,
    showMapContextControls = false,
    className = '',
}) => {
    const active = countActiveEditorFilters(value);
    const canUseNonMatchingContext = active > 0;

    useEffect(() => {
        if (!canUseNonMatchingContext && value.mapContextMode === 'non_matching') {
            onChange({ ...value, mapContextMode: 'none' });
        }
    }, [canUseNonMatchingContext, value, onChange]);

    const clearAll = () => {
        onChange({ ...DEFAULT_PARKING_LOTS_EDITOR_FILTERS });
    };

    return (
        <div
            className={`flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2 ${className}`}
        >
            <div className="flex items-center gap-1.5 text-gray-600">
                <Filter className="h-4 w-4 shrink-0" />
                <span className="text-xs font-medium uppercase tracking-wide">Filtre</span>
                {active > 0 && (
                    <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-800">
                        {active}
                    </span>
                )}
            </div>

            <fieldset className="min-w-0">
                <legend className="sr-only">Parkeringstype</legend>
                <div className="flex flex-wrap gap-2">
                    {PARKING_TYPE_OPTIONS.map(({ value: v, label }) => (
                        <label
                            key={v}
                            className="inline-flex cursor-pointer items-center gap-1.5 rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-800 shadow-sm hover:bg-gray-50"
                        >
                            <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                checked={value.parkingTypes.includes(v)}
                                onChange={() => onChange(toggleParkingType(value, v))}
                            />
                            {label}
                        </label>
                    ))}
                </div>
            </fieldset>

            <label className="flex min-w-[8rem] flex-col gap-0.5">
                <span className="text-xs text-gray-600">Ejer</span>
                <input
                    type="text"
                    value={value.owner}
                    onChange={(e) => onChange({ ...value, owner: e.target.value })}
                    placeholder="Søg ejer…"
                    className="w-full min-w-[10rem] rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </label>

            <label className="flex flex-col gap-0.5">
                <span className="text-xs text-gray-600">I app</span>
                <select
                    value={value.displayInApp}
                    onChange={(e) =>
                        onChange({
                            ...value,
                            displayInApp: e.target.value as ParkingLotsEditorFiltersState['displayInApp'],
                        })
                    }
                    className="rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <option value="all">Alle</option>
                    <option value="true">Synlig</option>
                    <option value="false">Skjult</option>
                </select>
            </label>

            <label className="flex flex-col gap-0.5">
                <span className="text-xs text-gray-600">Live data</span>
                <select
                    value={value.liveData}
                    onChange={(e) =>
                        onChange({
                            ...value,
                            liveData: e.target.value as ParkingLotsEditorFiltersState['liveData'],
                        })
                    }
                    className="rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <option value="all">Alle</option>
                    <option value="true">Med live</option>
                    <option value="false">Uden live</option>
                </select>
            </label>

            {showMapContextControls && (
                <label className="flex min-w-[12rem] flex-col gap-0.5">
                    <span className="text-xs text-gray-600">Kontekst på kort</span>
                    <select
                        value={value.mapContextMode}
                        onChange={(e) =>
                            onChange({
                                ...value,
                                mapContextMode: e.target.value as MapContextMode,
                            })
                        }
                        className="rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="none">Ingen</option>
                        <option value="paid">Alle betalte (sammenlign)</option>
                        <option value="non_matching" disabled={!canUseNonMatchingContext}>
                            Ikke-primær (hele byen minus filter)
                        </option>
                    </select>
                    {value.mapContextMode !== 'none' && (
                        <span className="text-[10px] leading-tight text-gray-500">
                            Viser et dæmpet lag ud over det primære filter.
                        </span>
                    )}
                </label>
            )}

            {active > 0 && (
                <button
                    type="button"
                    onClick={clearAll}
                    className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                >
                    <X className="h-3.5 w-3.5" />
                    Nulstil
                </button>
            )}
        </div>
    );
};

export default ParkingLotsEditorFilters;
